import type { Section } from '../zod.js';
import type { WizardOptionCopy } from './types.js';

/**
 * Read-aloud copy for each Section. Used by SectionsStep + the printable
 * client leave-behind to explain "should we include this section?".
 */
export const SECTION_COPY: Readonly<Record<Section, WizardOptionCopy>> = {
  Hero: {
    name: 'Hero',
    oneLiner: 'The first thing every visitor sees - your headline, your promise, your photo.',
    clientPitch:
      "The hero is the front door. It has about three seconds to do four jobs: tell people who you are, what you do, why they should care, and what to do next. Every shell composes it differently, but it's always the most important section on the site.",
    bestFor: ['Always include', 'Every site needs one', 'The single most-read section'],
    avoidIf: ['Never skip this'],
    feel: ['Essential', 'First impression'],
  },
  Services: {
    name: 'Services',
    oneLiner: 'What you actually do, broken into 3-6 bite-sized cards.',
    clientPitch:
      "We translate your offerings into a quick-scan grid. Each card has a title, a short description, and an optional starting price. Visitors should be able to figure out whether you sell what they need in under 10 seconds.",
    bestFor: ['Multi-service businesses', 'Trades', 'Professional services', 'Productized offerings'],
    avoidIf: ['You sell only one thing and the hero already explains it'],
    feel: ['Practical', 'Scannable', 'Decisive'],
  },
  Testimonials: {
    name: 'Testimonials',
    oneLiner: 'Real quotes from real clients with names and roles.',
    clientPitch:
      "Other people saying you're good is more believable than you saying you're good. We feature 2-4 quotes from clients who'll vouch for you, with their name and role. Even one strong testimonial moves people who are on the fence.",
    bestFor: ['Service businesses', 'New brands building trust', 'Anything sold by reputation'],
    avoidIf: ['You can\'t get permission to use names + faces', 'You have zero clients yet'],
    feel: ['Trust-building', 'Human'],
  },
  Pricing: {
    name: 'Pricing',
    oneLiner: 'Three tiers people can compare at a glance.',
    clientPitch:
      "We'll show three packages side by side with the middle one highlighted as the most popular. Even if your real pricing is a quote-based conversation, this teaches visitors what 'good / better / best' looks like and removes the awkward 'how much?' moment.",
    bestFor: ['Service businesses with productized offers', 'Trades with package pricing', 'Anyone fighting price-shopping'],
    avoidIf: ['You truly never quote price (luxury / bespoke)', 'Your engagement is hourly with no anchor'],
    feel: ['Transparent', 'Confident', 'Decisive'],
  },
  Contact: {
    name: 'Contact',
    oneLiner: 'Form on one side, phone / email / hours / map on the other.',
    clientPitch:
      "The conversion section. People who got this far want to talk to you. We make it absurdly easy - one form, your phone number bigger than usual, your hours, and a map. No friction, no surprise fields, no captcha-from-hell.",
    bestFor: ['Always include', 'Every business with a customer'],
    avoidIf: ['Online-only sales with no support need'],
    feel: ['Welcoming', 'Frictionless', 'Reachable'],
  },
  SocialProof: {
    name: 'Social Proof',
    oneLiner: 'Star ratings + review counts + recognizable badges.',
    clientPitch:
      "Quick visual trust signals: your Google rating, your review count on the relevant platform, BBB or industry badges if you have them. Sits right under the hero so people see 'oh, real business, real reputation' before they even start reading.",
    bestFor: ['Service businesses with reviews', 'Local businesses', 'Anyone with strong third-party validation'],
    avoidIf: ['You\'re too new for reviews yet (better to skip than fake it)'],
    feel: ['Reassuring', 'Quick-scan'],
  },
  Logo: {
    name: 'Logo',
    oneLiner: 'A standalone tribute to your mark - rare, only when the logo is exceptional.',
    clientPitch:
      "Most sites don't need this - the logo lives in the header and that's enough. But occasionally a logo is so good (or so historic) that giving it its own moment is the right move. We'll only suggest this when it earns the space.",
    bestFor: ['Heritage brands', 'Studios with iconic marks', 'Branding-led businesses'],
    avoidIf: ['Your logo is functional but not iconic', 'You want the whole page to be content'],
    feel: ['Reverent', 'Brand-led'],
  },
  Gallery: {
    name: 'Gallery',
    oneLiner: 'A scrollable / hoverable visual showcase of your work.',
    clientPitch:
      "If your work is photographable, show it. We give you a gallery that hovers to zoom on desktop and snaps to scroll on mobile. People who came to see what you make get to see what you make.",
    bestFor: ['Photographers', 'Designers', 'Restaurants', 'Salons', 'Renovation / construction', 'Retail'],
    avoidIf: ['Your work is invisible (consulting, software backend)', 'You don\'t have great photos yet'],
    feel: ['Visual', 'Generous', 'Browsable'],
  },
  BeforeAfter: {
    name: 'Before & After',
    oneLiner: 'Side-by-side proof of the transformation you create.',
    clientPitch:
      "When the value of your work is the difference between two states - before and after - showing both is the most convincing thing you can do. We use clear ribbons over each photo so there's no confusion about which is which.",
    bestFor: ['Renovation', 'Cleaning', 'Landscaping', 'Detailing', 'Anything where you transform something'],
    avoidIf: ['Your value isn\'t a visual transformation'],
    feel: ['Proof', 'Confidence-building', 'Concrete'],
  },
  ValueProps: {
    name: 'Value Props',
    oneLiner: 'A 3-tile strip showing the top reasons to choose you.',
    clientPitch:
      "Three short tiles, each with an icon and a one-line proof point - 'On-time guaranteed', 'Family-owned', 'Locally licensed'. It's the second-page sales pitch, but visible immediately. Helps people who are already half-sold confirm their gut.",
    bestFor: ['Most service businesses', 'Local businesses', 'Anywhere there are 3 quick reasons to pick you'],
    avoidIf: ['Your value is too subtle to compress to 3 lines'],
    feel: ['Quick-win', 'Reinforcing'],
  },
  FAQ: {
    name: 'FAQ',
    oneLiner: 'The 4-6 questions every prospect actually asks.',
    clientPitch:
      "Pre-empt the questions people would otherwise call to ask. Answering them on the page builds trust AND saves you time. Bonus: it's great for SEO because it captures long-tail searches.",
    bestFor: ['Service businesses with consistent pre-sales questions', 'Anyone fielding the same calls', 'SEO benefit'],
    avoidIf: ['Your sales process is genuinely bespoke per inquiry'],
    feel: ['Helpful', 'Efficient', 'Trust-building'],
  },
};
